import { Component, OnInit, Input, Output, EventEmitter, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { FormControl, FormGroup, FormBuilder, Validators } from '@angular/forms';

import { Observable, Subject, ReplaySubject, BehaviorSubject } from 'rxjs';

import { RxComponent } from '../common/rx.component';

declare var $: any;

@Component({
    selector: 'itpx-flexible-dropdown',
    templateUrl: './flexible-dropdown.component.html',
    styleUrls: ['./flexible-dropdown.component.css']
})
export class FlexibleDropdownComponent extends RxComponent implements OnChanges {

    private readonly EMPTY = {};

    @ViewChild('formContainer') formElement;

    @Input()
    private outerForm: FormGroup;
    private form: FormGroup;
    @Input()
    private name: string;
    @Input()
    private config: FlexibleDropdownConfig;
    private toggleFormClick = new Subject<boolean>();
    private addNew = new Subject<any>();
    private itemChange = new Subject<string>();
    private configInit = new ReplaySubject<FlexibleDropdownConfig>(1);

    @Output()
    private success = new EventEmitter<any>();
    @Output()
    private selectChange = new EventEmitter<any>();
    private formToggled: Observable<boolean>;
    private items: Observable<any[]>;
    private currentItem: Observable<any>;
    private formState: Observable<any>;
    private loading: boolean;

    constructor(private fb: FormBuilder) {
        super();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['config']) {
            this.configInit.next(this.config);
        }
    }

    ngOnInit() {

        let dropdownConfig = this.configInit 
            .filter(config => !!config)
            .take(1);
        
        dropdownConfig
            .bindToLifecycle(this)
            .subscribe(config => this.form = this.buildForm(config.questions));

        let itemAdded = this.addNew
            .flatMap(data => this.addItem(data))
            .cacheResult();

        this.formToggled = Observable.merge(this.toggleFormClick, itemAdded)
            .throttleTime(250)
            .scan((toggled, _) => !toggled, false)
            .startWith(false)
            .cacheResult();

        this.formToggled
            .skip(1)
            .bindToLifecycle(this)        
            .subscribe(toggled => {
                if (toggled) {
                    $(this.formElement.nativeElement).slideDown(250);
                } else {
                    $(this.formElement.nativeElement).slideUp(250);
                }
            });

        this.items = dropdownConfig
            .flatMap(config => Observable.merge(config.forceRefresh, itemAdded).startWith({}))
            .switchMap(_ => this.config.dictionaryFactory().neverError())
            .shareReplay();

        let itemSelect = this.itemChange.switchMap(key => this.items
            .map(items => items.find(i => i.key == key))
            .take(1)
        );

        this.currentItem = dropdownConfig
            .flatMap(config => Observable.merge(itemSelect, itemAdded)
                .startWith(this.config.initialItem || this.EMPTY)
            )
            .shareReplay();

        this.currentItem
            .skipWhile(i => i == this.EMPTY)
            .bindToLifecycle(this)
            .subscribe(item => this.selectChange.emit(item));

        itemAdded
            .bindToLifecycle(this)
            .subscribe(_ => {
                this.form.reset();
                this.success.emit(true);
            });
    }

    private buildForm(questions: any[]): FormGroup {
        let form = this.fb.group({});
        for (let question of questions) {
            let control = new FormControl();
            if (question.required) {
                control.setValidators([Validators.required]);
            }

            form.addControl(question.key, control);
        }

        return form;
    }

    private addItem(data: any): Observable<any[]> {
        this.loading = true;
        return this.config.addFactory(data)
            .take(1)
            .doOnEach(() => this.loading = false)
            .neverError();
    }

    private onFormToggle() {
        this.toggleFormClick.next(false);
    }

    private onItemChange(key: string) {
        this.itemChange.next(key);
    }

    private onSubmit({ value, valid }: { value: any, valid: boolean }) {
        this.addNew.next(value);
    }
}

export class FlexibleDropdownConfig {

    label: string;
    questions: Array<any>;
    initialItem: any;
    required: boolean;
    placeholder: string;
    dictionaryFactory: () => Observable<any[]>;
    addFactory: (formData: any) => Observable<any>;
    addLabel: string;
    forceRefresh: Observable<any>;
    hideForm: boolean;
}



// WEBPACK FOOTER //
// ./src/app/shared/components/flexible-dropdown/flexible-dropdown.component.ts